/**
 * Triggers Service - Fetches trigger metadata for tables
 */

import type { DatabaseEngineId } from '@/core/engines';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';

export interface TriggerInfo {
  name: string;
  timing: string;
  events: string;
  isEnabled: boolean;
}

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function toBool(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
  return false;
}

function getRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Parse timing and event from a SQLite CREATE TRIGGER SQL statement.
 * Pattern: CREATE TRIGGER [IF NOT EXISTS] name (BEFORE|AFTER|INSTEAD OF) (DELETE|INSERT|UPDATE [OF cols]) ON table
 */
export function parseSqliteTriggerSql(sql: string): { timing: string; event: string } {
  if (!sql) return { timing: '', event: '' };
  const timingMatch = sql.match(/\b(BEFORE|AFTER|INSTEAD\s+OF)\b/i);
  const eventMatch = sql.match(/\b(DELETE|INSERT|UPDATE)\b/i);
  return {
    timing: timingMatch ? timingMatch[1].toUpperCase().replace(/\s+/, ' ') : '',
    event: eventMatch ? eventMatch[1].toUpperCase() : '',
  };
}

export function mapTriggerInfoRows(rows: Record<string, unknown>[]): TriggerInfo[] {
  return rows
    .map((row) => {
      const name = toStr(getRowValue(row, ['trigger_name']));
      if (!name) return null;

      let timing = toStr(getRowValue(row, ['trigger_timing']));
      let events = toStr(getRowValue(row, ['trigger_events']));
      const isEnabled = toBool(getRowValue(row, ['is_enabled']));

      // SQLite: timing/events are empty; parse from trigger_sql column
      if (!timing && !events) {
        const triggerSql = toStr(getRowValue(row, ['trigger_sql', 'sql']));
        if (triggerSql) {
          const parsed = parseSqliteTriggerSql(triggerSql);
          timing = parsed.timing;
          events = parsed.event;
        }
      }

      return { name, timing, events, isEnabled };
    })
    .filter((t): t is TriggerInfo => t !== null);
}

/**
 * Format a trigger's display name in SSMS style
 * e.g. "TR_AuditInsert (AFTER INSERT, UPDATE)" or "TR_Disabled (AFTER DELETE) [disabled]"
 */
export function formatTriggerDisplayName(trigger: TriggerInfo): string {
  const parts: string[] = [];
  if (trigger.timing) parts.push(trigger.timing);
  if (trigger.events) parts.push(trigger.events);

  const suffix = parts.length > 0 ? ` (${parts.join(' ')})` : '';
  const disabled = trigger.isEnabled ? '' : ' [disabled]';

  return `${trigger.name}${suffix}${disabled}`;
}

export async function getTableTriggers(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  engine?: DatabaseEngineId | string
): Promise<{ triggers: TriggerInfo[]; error?: string }> {
  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.tableTriggers(schema, tableName),
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[TriggersService] Error getting triggers: ${errorMsg}`);
      return { triggers: [], error: errorMsg };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const triggers = mapTriggerInfoRows(rows);

    return { triggers };
  } catch (error) {
    console.error('[TriggersService] Error in getTableTriggers:', error);
    return {
      triggers: [],
      error: error instanceof Error ? error.message : 'Unknown error getting triggers',
    };
  }
}
